import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { FormRow, FormSection, Switch, Tabs } from "@omnabase/ui";
import { effectiveHours, hasWorkingBlock, hoursSummary, type Notify } from "@omnabase/shared";
import { useCan } from "../../lib/access";
import { api } from "../../lib/api";
import { NOTIFY_EVENTS, channelAllOn, isOn, notifyChannel, setChannel, setOn, type NotifyChannel } from "../../lib/notify";
import { deviceTabShown } from "../../lib/pushCard";
import { notifyTabParam, notifyTabsFor, resolveNotifyTab, type NotifyTabId } from "../../lib/notifyTabs";
import { useNav, useSetParam, useWs } from "../../lib/nav";
import { useScrollReset } from "../../lib/scrollReset";
import { DevicePanel, YourDevices, useDevicePush, type DevicePush } from "./DevicePanel";
import { useSaver } from "../../lib/save";
import { useSession } from "../../shell/store";

// Settings › Notifications: what reaches the signed-in person, and where. Ported; stored now, sent from M3.
// 2026-10-17: which tickets of a project reach the inbox is set on the project's People tab, no longer here.
// 2026-10-17, the notifications honesty round: the Email column alone (only email sends; no in-app bell is planned, the badges are the
// in-app notification), no spend-cap row (owners get the credits mails regardless).
// 2026-10-18, browser notifications round one: the card above the grid, the second channel beside Email, and the quiet row holds notifications
// of both kinds.
// 2026-10-29, the notification mail excerpt round: the workspace's switch for what a notification mail carries, administrators change it,
// members see it locked with its state.
// 2026-11-01, the device notifications round (brief docs/briefs/device-notifications-round.md, DECISIONS "2026-11-01 — Device notifications"):
// three tabs under a fixed head, as on the AI page (`lib/notifyTabs.ts`): Device (the panel, "Your devices", the Device switches), Email (the
// Email switches, the owners' line, the mail row) and Quiet times (the hold switch, which covers both channels; since 2026-11-01 with the
// person's working hours under it, the daily summary mail gone). No
// switch sits beside a switch of the other channel; nothing is repeated across tabs.
export function NotificationSettings() {
  const { workspace } = useSession();
  const [params] = useSearchParams(); const setParam = useSetParam();
  const push = useDevicePush(workspace.slug);
  const notify = useNotify(); // one state and one saver for both channels' tabs: a quick tab switch never saves a stale copy over a fresh one
  const tabs = notifyTabsFor(deviceTabShown(push.state));
  const tab = resolveNotifyTab(params.get("tab"), tabs);
  const top = useScrollReset(tab); // a tab starts at the top
  return (
    <main className="ob-profile" aria-label="Notifications">
      <header className="ob-profile__head">
        <div className="ob-settings__head"><div><h1 className="ob-page__title">Notifications</h1><p className="ob-page__subtitle">What reaches you, and where.</p></div></div>
        <Tabs variant="line" items={tabs} active={tab} onChange={(id) => setParam("tab", notifyTabParam(id, tabs))} />
      </header>
      <div className="ob-settings__scroll" ref={top}><div className="ob-form">
        <NotifyTab tab={tab} push={push} notify={notify} deviceShown={tabs[0]?.id === "device"} />
      </div></div>
    </main>
  );
}

/** one tab's content */
function NotifyTab({ tab, push, notify, deviceShown }: { tab: NotifyTabId; push: DevicePush; notify: NotifyState; deviceShown: boolean }) {
  if (tab === "quiet") return <QuietTimes notify={notify} />;
  if (tab === "email") return <EmailSwitches notify={notify} dotsHere={!deviceShown} />;
  return <><DevicePanel push={push} /><YourDevices push={push} /><DeviceSwitches notify={notify} /></>;
}

type NotifyState = { n: Notify; set: (next: Notify) => void };
/** the person's switches and their saving, shared by every tab */
function useNotify(): NotifyState {
  const { workspace, reload, toast } = useSession();
  const [n, setN] = useState<Notify>(workspace.me.notify);
  useEffect(() => setN(workspace.me.notify), [workspace.me.notify]);
  const { save } = useSaver<{ notify: Notify }>(async (patch) => { await api.patch(`/api/v1/w/${workspace.slug}/me/settings`, patch); await reload(); }, toast);
  const set = (next: Notify) => { setN(next); save({ notify: next }); };
  return { n, set };
}

/** the line on what the dots show (the chat dot round, 2026-11-01), word for word; on the Device tab, or on Email without one */
function DotsLine() {
  return <p className="ob-form-row__help ob-notifygrid__note">While OmnaBase is open, the Inbox and Team chat dots show what is unread; Team chat&apos;s dot is grey for any message, coloured for mentions and direct messages.</p>;
}

/** the Device tab's "Tell me when": the six events with the Device switches (stored as the `browser` channel), the dots line, its own line */
function DeviceSwitches({ notify }: { notify: NotifyState }) {
  return (
    <FormSection title="Tell me when" intro="Each switch shows a notification on every device you turned on.">
      <ChannelTable channel="browser" notify={notify} />
      <DotsLine />
      <p className="ob-form-row__help ob-notifygrid__note">These switches decide notifications on your devices only, never the dots.</p>
    </FormSection>
  );
}

/** the Email tab's "Tell me when": the six events with the Email switches, its own line, the owners' credits line, the mail row */
function EmailSwitches({ notify, dotsHere }: { notify: NotifyState; dotsHere: boolean }) {
  const { workspace } = useSession();
  return (
    <FormSection title="Tell me when" intro="Each switch sends you a mail when it happens.">
      <ChannelTable channel="email" notify={notify} />
      {dotsHere && <DotsLine />}
      <p className="ob-form-row__help ob-notifygrid__note">These switches decide mails only, never the dots.</p>
      {workspace.me.role === "owner" && <p className="ob-form-row__help ob-notifygrid__note">Owners always get the credits mails.</p>}
      <MailPreviewRow />
    </FormSection>
  );
}

/** the kit's table: one row per event, one switch column for the tab's channel with its "all events" switch in the header */
function ChannelTable({ channel, notify: { n, set } }: { channel: NotifyChannel; notify: NotifyState }) {
  const ids = NOTIFY_EVENTS.map((e) => e.id);
  const { label } = notifyChannel(channel);
  return (
    <div className="ob-dtable-card">
      <table className="ob-dtable ob-notifygrid">
        <thead><tr><th>Event</th><th className="ob-dtable__center"><span className="ob-notifygrid__col">{label}<Switch checked={channelAllOn(n, channel, ids)} label={`${label}: all events`} onChange={(v) => set(setChannel(n, channel, ids, v))} /></span></th></tr></thead>
        <tbody>
          {NOTIFY_EVENTS.map((e) => (
            <tr key={e.id}>
              <td className="ob-dtable__grow ob-notifygrid__event"><span className="ob-dtable__name">{e.label}</span><span className="ob-dtable__email">{e.desc}</span></td>
              <td className="ob-dtable__center"><Switch checked={isOn(n, e.id, channel)} label={`${e.label}: ${label}`} onChange={(v) => set(setOn(n, e.id, channel, v))} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** the Quiet times tab ("quiet"): the hold switch covering both channels, the person's working hours under it with a link to where they change
 *  (the working hours round, 2026-11-01: the hours are the person's own, else the workspace's default, in their own zone; the daily summary mail
 *  left), and the away line */
function QuietTimes({ notify: { n, set } }: { notify: NotifyState }) {
  const { workspace } = useSession();
  return (
    <FormSection title="Quiet times">
      <FormRow label="Hold notifications outside my working hours" help="What arrives outside them waits in the inbox and reaches you when your next working hour starts.">
        <Switch checked={n.quiet} onChange={(v) => set({ ...n, quiet: v })} label="Hold notifications outside my working hours" />
      </FormRow>
      <MyHoursLine />
      {workspace.me.away && <p className="ob-form-row__help">You are away: notifications are held until you are back.</p>}
    </FormSection>
  );
}

/** "Your working hours: Mon to Fri, 09:00 to 17:00 (the workspace default) · Change", or the sentence for a week without any block */
function MyHoursLine() {
  const { workspace } = useSession();
  const nav = useNav(); const { ws } = useWs();
  const mine = workspace.members.find((m) => m.id === workspace.me.memberId);
  const own = mine?.hours ?? null;
  const hours = effectiveHours(own, workspace.settings.hours);
  const change = <a className="ob-link" href={ws("/settings/team/hours")} onClick={(e) => nav(ws("/settings/team/hours"), e)}>Change</a>;
  if (!hasWorkingBlock(hours)) return <p className="ob-form-row__help">You have no working hours set, so nothing is held · {change}</p>;
  return <p className="ob-form-row__help">Your working hours: {hoursSummary(hours)} ({own ? "your own" : "the workspace default"}) · {change}</p>;
}

/** the workspace's switch (settings.notifyPreview): the AI's fresh summary or the customer's first words in every notification mail */
function MailPreviewRow() {
  const { workspace, reload, toast } = useSession();
  const can = useCan()("aiSettings");
  const [on, setOn] = useState(workspace.settings.notifyPreview);
  useEffect(() => setOn(workspace.settings.notifyPreview), [workspace.settings.notifyPreview]);
  const { save } = useSaver<{ notifyPreview: boolean }>(async (patch) => { await api.patch(`/api/v1/w/${workspace.slug}/settings`, patch); await reload(); }, toast);
  return (
    <FormRow label="Notification mails carry the message's first lines" audience="locked" help="The AI's summary, or the first lines of the newest message, at the top of each notification mail.">
      <Switch checked={on} onChange={(v) => { setOn(v); save({ notifyPreview: v }); }} label="Notification mails carry the message's first lines" disabled={!can} />
    </FormRow>
  );
}
